import { useRef, useState } from 'preact/hooks'
import { ImagePlus, X } from 'lucide-preact'
import type { LandingPageContentImageDto } from '@/types/landingPage'
import { BRAND_CLASSES } from '@/constants/theme'
import { landingPagesApi } from '@/api/landingPages'

const MAX_CONTENT_IMAGE_BYTES = 5 * 1024 * 1024
const ALLOWED_CONTENT_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

interface PageImagesSectionProps {
  /** Current content images. Held by the parent so notes ride into the Generate body. */
  contentImages: LandingPageContentImageDto[]
  /** Called with the next list whenever an image is added, removed, or its note edited. */
  onChange: (next: LandingPageContentImageDto[]) => void
  /** Workspace ID for upload API calls. */
  workspaceId?: string
  /** Web ID for upload API calls. */
  webId?: string
  /** Disable all inputs (read-only / locked once the page is submitted). */
  disabled?: boolean
}

/**
 * "Page Images" sidebar section. A multi-image uploader for user-supplied
 * content images (ORB-413). Uploads run pre- or post-generation; pre-generation
 * the server stages the blob and the dashboard carries the image records into
 * the Generate request body. Each card carries an optional Note hint Webster
 * uses to place the image. Images lock once the page is submitted for review.
 */
export function PageImagesSection({ contentImages, onChange, workspaceId, webId, disabled }: PageImagesSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const canUpload = !!workspaceId && !!webId

  const handleUpload = async (files: FileList) => {
    if (!workspaceId || !webId) return
    setUploadError(null)
    setUploading(true)
    try {
      const added: LandingPageContentImageDto[] = []
      for (const file of Array.from(files)) {
        if (!ALLOWED_CONTENT_IMAGE_MIME_TYPES.includes(file.type)) {
          setUploadError('Images must be JPG, PNG, WebP, or GIF')
          continue
        }
        if (file.size > MAX_CONTENT_IMAGE_BYTES) {
          setUploadError('Each image must be under 5MB')
          continue
        }
        const res = await landingPagesApi.uploadContentImage(workspaceId, webId, file)
        if (res.data) {
          added.push(res.data.ContentImage)
        } else {
          setUploadError(res.error || 'Failed to upload image')
        }
      }
      if (added.length > 0) onChange([...contentImages, ...added])
    } catch {
      setUploadError('Failed to upload image')
    } finally {
      setUploading(false)
    }
  }

  const handleFileChange = (e: Event) => {
    const target = e.target as HTMLInputElement
    if (target.files && target.files.length > 0) handleUpload(target.files)
    target.value = ''
  }

  const handleNoteChange = (id: string, note: string) => {
    onChange(contentImages.map((img) => (img.Id === id ? { ...img, Note: note } : img)))
  }

  const handleRemove = (id: string) => {
    onChange(contentImages.filter((img) => img.Id !== id))
  }

  const noteInputClass = `w-full text-xs px-2 py-1 rounded border ${BRAND_CLASSES.INPUT.NORMAL_BORDER} ${BRAND_CLASSES.INPUT.NORMAL_BG} text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50`
  const uploadBtnClass = `flex items-center justify-center gap-2 w-full px-3 py-2 text-sm border-2 border-dashed rounded-lg transition-colors border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 disabled:opacity-50 disabled:pointer-events-none`

  return (
    <div className="space-y-3">
      {uploadError && (
        <p className="text-sm text-red-600 dark:text-red-400">{uploadError}</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />
      <button
        type="button"
        disabled={disabled || uploading || !canUpload}
        onClick={() => inputRef.current?.click()}
        className={uploadBtnClass}
      >
        <ImagePlus className="w-4 h-4 text-slate-500 dark:text-slate-400" />
        <span className="text-slate-600 dark:text-slate-300">
          {uploading ? 'Uploading...' : 'Add images'}
        </span>
      </button>

      {contentImages.length > 0 && (
        <ul className="space-y-2">
          {contentImages.map((img) => (
            <li
              key={img.Id}
              className="flex items-start gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-2"
            >
              <img
                src={img.Url}
                alt={img.FileName || 'Page image'}
                className="h-14 w-14 rounded object-cover border border-slate-200 dark:border-slate-600 bg-white shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="text-xs text-slate-600 dark:text-slate-300 truncate" title={img.FileName}>
                  {img.FileName || 'Image'}
                </div>
                <input
                  type="text"
                  value={img.Note ?? ''}
                  disabled={disabled}
                  placeholder="Note (optional) — what is this / where it goes"
                  onInput={(e) => handleNoteChange(img.Id, (e.target as HTMLInputElement).value)}
                  className={`${noteInputClass} mt-1`}
                />
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={() => handleRemove(img.Id)}
                className="p-1 rounded text-slate-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                title="Remove image"
                aria-label="Remove image"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-slate-400 dark:text-slate-500">
        Images lock once the page is submitted for review.
      </p>
    </div>
  )
}
